import React, { Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import { jwtDecode } from "jwt-decode";

import App from "./pages/App";
import TeacherProfile from "./pages/TeacherProfile";

import MasterDashboard from "./pages/MasterDashboard";
import SuperSecretInternalDash from "./pages/SuperSecretInternalDash";
import Reports from "./pages/Reports";
import AssessmentReports from "./pages/AssessmentReports";

import "./fonts/Adia_Demo.ttf";

import "./index.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import UserExistence from "./pages/UserExistence";
import ResettingTasks from "./pages/ResettingTasks";
import SchoolAdmin from "./pages/SchoolAdmin";
import ClassAdmin from "./pages/ClassAdmin";
import InternalAdmin from "./pages/InternalAdmin";
import AladdinExport from "./pages/AladdinExport";
import TrialManagement from "./pages/TrialManagement";
import PurchaseManagement from "./pages/PurchaseManagement";
import ScreenerManagement from "./pages/ScreenerManagement";
import PilotManagement from "./pages/PilotManagement";
import TrialTrackerDash from "./pages/TrialTrackerDash";
import SchoolMessagingDash from "./pages/SchoolMessagingDash";
import SandboxContentManagement from "./pages/SandboxContentManagement";
import BannerContentAdmin from "./pages/BannerContentAdmin";
import VisualBannerEditor from "./pages/VisualBannerEditor";
import ProductCatalog from "./pages/ProductCatalog";
import SchoolAccessManager from "./pages/SchoolAccessManager";
import OnboardingTrackerDash from "./pages/OnboardingTrackerDash";
import TagManagement from "./pages/TagManagement";
import SchoolGroupAdmin from "./pages/SchoolGroupAdmin";
import RecommendationsAdmin from "./pages/RecommendationsAdmin";
import MessageEditor from "./pages/MessageEditor";
import CustomQRGenerator from "./pages/CustomQRGenerator";
import AcademicYearControl from "./pages/AcademicYearControl";
import StaffAccessManager from "./pages/StaffAccessManager";

const container = document.getElementById("root");
const root = createRoot(container);

function ProtectedRoute({ element }) {
  const location = useLocation();
  
  // Check if there's an incoming token in the URL
  const urlParams = new URLSearchParams(location.search);
  const incomingToken = urlParams.get('token');
  const source = urlParams.get('source');
  
  // If there's a token from TD in the URL, let it through (TokenHandler will process it)
  if (incomingToken && source === 'td') {
    console.log("📥 Incoming token detected from TD, allowing access...");
    return element;
  }
  
  // Otherwise, check localStorage for existing token
  const token = localStorage.getItem("token");
  console.log("Checking token...");

  if (!token) {
    console.log("No token found, redirecting to home page.");
    return <Navigate to="/" replace />;
  }

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decodedToken.exp < currentTime) {
      console.log(
        "Token expired, removing token and redirecting to home page."
      );
      localStorage.removeItem("token");
      return <Navigate to="/" replace />;
    }

    console.log("Token is valid, rendering element.");
  } catch (e) {
    console.error(
      "Token decoding failed or invalid token, redirecting to home page."
    );
    return <Navigate to="/" replace />;
  }

  return element;
}

// Token Handler Component
function TokenHandler() {
  const location = useLocation();

  useEffect(() => {
    // Check for incoming token from TD
    const urlParams = new URLSearchParams(location.search);
    const incomingToken = urlParams.get('token');
    const schoolId = urlParams.get('schoolId');
    const source = urlParams.get('source');

    if (incomingToken && source === 'td') {
      console.log('📥 Received token from TD:', { schoolId, source });
      
      try {
        // Validate token format before storing
        jwtDecode(incomingToken);
        console.log('✅ Token decoded successfully');
        
        // Store the token in localStorage
        localStorage.setItem('token', incomingToken);
        
        // Store context about where they came from
        localStorage.setItem('source_app', 'td');
        localStorage.setItem('teacher_school_id', schoolId);
        
        console.log('💾 Token and context stored in localStorage');
        
        // Clean URL (remove token from address bar for security) while keeping workflow launch context.
        urlParams.delete('token');
        const cleanSearch = urlParams.toString();
        const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
        window.history.replaceState({}, '', cleanUrl);
        
        console.log('🧹 URL cleaned');
        
      } catch (error) {
        console.error('❌ Invalid token received from TD:', error);
      }
    }
  }, [location]);

  return null;
}

function RootComponent() {
  useEffect(() => {
    window.HubSpotConversations?.widget?.refresh();
    console.log("HubSpot widget refreshed.");
  }, []);

  return (
    <BrowserRouter forceRefresh={true}>
      <TokenHandler />
      <Suspense
        fallback={
          <div className="spinner-container">
            <div className="big-ball-spinner"></div>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<App />} />
          <Route
            path="/masterdashboard"
            element={<ProtectedRoute element={<MasterDashboard />} />}
          />
          <Route
            path="/profile"
            element={<ProtectedRoute element={<TeacherProfile />} />}
          />
          <Route
            path="/supersecretinternaldash"
            element={<ProtectedRoute element={<SuperSecretInternalDash />} />}
          />
          <Route
            path="/reports"
            element={<ProtectedRoute element={<Reports />} />}
          />
          <Route
            path="/assessmentreports"
            element={<ProtectedRoute element={<AssessmentReports />} />}
          />
          <Route
            path="/userexistence"
            element={<ProtectedRoute element={<UserExistence />} />}
          />

          <Route
            path="/resettasks"
            element={<ProtectedRoute element={<ResettingTasks />} />}
          />

          <Route
            path="/schooladmin"
            element={<ProtectedRoute element={<SchoolAdmin />} />}
          />

          <Route
            path="/classadmin"
            element={<ProtectedRoute element={<ClassAdmin />} />}
          />

          <Route
            path="/internaladmin"
            element={<ProtectedRoute element={<InternalAdmin />} />}
          />

          <Route
            path="/aladdinexport"
            element={<ProtectedRoute element={<AladdinExport />} />}
          />

           <Route
            path="/trialmanagement"
            element={<ProtectedRoute element={<TrialManagement />} />}
          />

          <Route
            path="/screenermanagement"
            element={<ProtectedRoute element={<ScreenerManagement />} />}
          />

          <Route
            path="/purchasemanagement"
            element={<ProtectedRoute element={<PurchaseManagement />} />}
          />

           <Route
            path="/pilotmanagement"
            element={<ProtectedRoute element={<PilotManagement />} />}
          />

          <Route
            path="/schoolmessagingdash"
            element={<ProtectedRoute element={<SchoolMessagingDash />} />}
          />

          <Route
            path="/trialtrackerdash"
            element={<ProtectedRoute element={<TrialTrackerDash />} />}
          />

          <Route
            path="/sandboxcontentmanagement"
            element={<ProtectedRoute element={<SandboxContentManagement />} />}
          />

           <Route
            path="/bannercontentadmin"
            element={<ProtectedRoute element={<BannerContentAdmin />} />}
          />

          <Route
            path="/visualbannereditor"
            element={<ProtectedRoute element={<VisualBannerEditor />} />}
          />

           <Route
            path="/productcatalog"
            element={<ProtectedRoute element={<ProductCatalog />} />}
          />

          <Route
            path="/schoolaccessmanager"
            element={<ProtectedRoute element={<SchoolAccessManager />} />}
          />

          <Route
            path="/onboardingtrackerdash"
            element={<ProtectedRoute element={<OnboardingTrackerDash />} />}
          />

           <Route
            path="/tagmanagement"
            element={<ProtectedRoute element={<TagManagement />} />}
          />

          <Route
            path="/schoolgroupadmin"
            element={<ProtectedRoute element={<SchoolGroupAdmin />} />}
          />

           <Route
            path="/recommendationsadmin"
            element={<ProtectedRoute element={<RecommendationsAdmin />} />}
          />

          <Route
            path="/messageeditor"
            element={<ProtectedRoute element={<MessageEditor />} />}
          />

          <Route
            path="/customqrgenerator"
            element={<ProtectedRoute element={<CustomQRGenerator />} />}
          />

          <Route
            path="/academicyearcontrol"
            element={<ProtectedRoute element={<AcademicYearControl />} />}
          />

          <Route
            path="/staffaccessmanager"
            element={<ProtectedRoute element={<StaffAccessManager />} />}
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

root.render(<RootComponent />);

reportWebVitals();
